import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@apollo/client/react';
import { Badge, Box, Button, Card, CloseButton, Dialog, Field, Flex, HStack, Icon, IconButton, Input, InputGroup, Portal, Stat, Table, Text, VStack } from '@chakra-ui/react';
import { EmptyState } from '@src/components/chakra-ui/empty-state';
import { NativeSelectField, NativeSelectRoot } from '@src/components/chakra-ui/native-select';
import { useAdvisorsCollectionLoadMore } from '@src/components/sco/Advisors/hooks/useAdvisorsCollectionLoadMore';
import { AdvisorsLoadMore } from '@src/components/sco/Advisors/shared/AdvisorsLoadMore';
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState';
import { advisorPayoutErrorMessage, advisorPayoutReviewer, advisorPayoutStatusLabel, advisorPayoutStatusPalette } from '@src/components/sco/Advisors/utils/advisorPayoutReview';
import { cleanErrorMessage, formatUsd, formatWhen } from '@src/components/sco/Advisors/utils/sessionFormat';
import { CANCEL_ADVISOR_PAYOUT } from '@src/gql/mutations/CancelAdvisorPayout';
import { CREATE_ADVISOR_CONNECT_DASHBOARD_LINK } from '@src/gql/mutations/CreateAdvisorConnectDashboardLink';
import { CREATE_ADVISOR_CONNECT_ONBOARDING_LINK } from '@src/gql/mutations/CreateAdvisorConnectOnboardingLink';
import { REFRESH_ADVISOR_CONNECT_STATUS } from '@src/gql/mutations/RefreshAdvisorConnectStatus';
import { REQUEST_ADVISOR_PAYOUT } from '@src/gql/mutations/RequestAdvisorPayout';
import { RESET_ADVISOR_STRIPE_CONNECT } from '@src/gql/mutations/ResetAdvisorStripeConnect';
import { ADVISOR_LEDGER_ENTRIES } from '@src/gql/queries/AdvisorLedgerEntries';
import { ADVISOR_PAYOUT_BALANCE } from '@src/gql/queries/AdvisorPayoutBalance';
import { ADVISOR_PAYOUTS } from '@src/gql/queries/AdvisorPayouts';
import { ScoAdvisorLedgerEntry, ScoAdvisorLedgerEntryCollection, ScoAdvisorPayout, ScoAdvisorPayoutBalance, ScoAdvisorPayoutCollection } from '@src/graphql-types';
import { ReactNode, useState } from 'react';
import { LuBanknote, LuExternalLink, LuHistory, LuRefreshCw, LuWallet } from 'react-icons/lu';
const PAGE_SIZE = 50;
const DEFAULT_PAYOUT_MIN_USD = 10;
type ConnectCountry = {
  code?: string | null;
  name?: string | null;
};
type BalanceData = {
  advisorPayoutBalance?: ScoAdvisorPayoutBalance | null;
};
type LedgerData = {
  advisorLedgerEntries?: ScoAdvisorLedgerEntryCollection | null;
};
type PayoutsData = {
  advisorPayouts?: ScoAdvisorPayoutCollection | null;
};
type OnboardingLinkData = {
  createAdvisorConnectOnboardingLink?: {
    url?: string | null;
  } | null;
};
type DashboardLinkData = {
  createAdvisorConnectDashboardLink?: {
    url?: string | null;
  } | null;
};
const connectStatusLabel = (status: string) => {
  if (status === 'enabled') {
    return 'Connected';
  }
  if (status === 'not_started') {
    return 'Not connected';
  }
  return status.replaceAll('_', ' ').replace(/\b\w/g, char => char.toUpperCase());
};
const connectStatusPalette = (status: string, connectEnabled: boolean) => {
  if (!connectEnabled) {
    return 'gray';
  }
  if (status === 'enabled') {
    return 'green';
  }
  if (status === 'not_started') {
    return 'orange';
  }
  return 'yellow';
};
export const PayoutConnectCard = t0 => {
  const $ = _c(64);
  const {
    connectStatus,
    connectEnabled,
    connectReady,
    isStandard,
    hasStripeAccount: t1,
    country: t2,
    countries: t3,
    stripeConnectCountry,
    onboardingLoading,
    refreshLoading,
    viewLoading,
    onCountryChange,
    onConnect,
    onRefresh,
    onRemove,
    onView
  } = t0;
  const hasStripeAccount = t1 === undefined ? false : t1;
  const country = t2 === undefined ? "" : t2;
  let countryName;
  let t4;
  if ($[0] !== stripeConnectCountry || $[1] !== t3) {
    const countries = t3 === undefined ? [] : t3;
    countryName = countries.find(option => option.code === stripeConnectCountry)?.name || stripeConnectCountry;
    let t5;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = {
        value: "",
        label: "Select a country"
      };
      $[4] = t5;
    } else {
      t5 = $[4];
    }
    t4 = [t5, ...countries.flatMap(_temp)];
    $[0] = stripeConnectCountry;
    $[1] = t3;
    $[2] = countryName;
    $[3] = t4;
  } else {
    countryName = $[2];
    t4 = $[3];
  }
  const countryItems = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Box bg="colorPalette.subtle" borderRadius="l2" color="colorPalette.fg" flexShrink={0} padding={2}><Icon boxSize={5}><LuWallet /></Icon></Box>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Text fontWeight="semibold">Stripe Connect</Text>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== connectEnabled || $[8] !== connectStatus) {
    t7 = connectStatusPalette(connectStatus, connectEnabled);
    $[7] = connectEnabled;
    $[8] = connectStatus;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== connectEnabled || $[11] !== connectStatus) {
    t8 = connectEnabled ? connectStatusLabel(connectStatus) : "Unavailable here";
    $[10] = connectEnabled;
    $[11] = connectStatus;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t7 || $[14] !== t8) {
    t9 = <HStack gap={2} wrap="wrap">{t6}<Badge colorPalette={t7} variant="subtle">{t8}</Badge></HStack>;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const t10 = !connectEnabled ? "Connect is disabled in this environment. Founders can still book you." : connectReady ? isStandard ? "Session payments settle on your Stripe account. Open the Stripe Dashboard to send money to your bank." : "Request a payout below once your available balance clears the holdback." : connectStatus === "not_started" ? "Founders can book you before this is set up. Connect Stripe when you want to withdraw earnings." : "Finish Stripe setup to withdraw. Founders can still book you.";
  let t11;
  if ($[16] !== t10) {
    t11 = <Text color="colorPalette.fg/60" fontFamily="longform" textStyle="sm">{t10}</Text>;
    $[16] = t10;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== countryName || $[19] !== hasStripeAccount) {
    t12 = hasStripeAccount && countryName ? <Text color="colorPalette.fg/60" fontFamily="longform" textStyle="sm">Payout country: {countryName}</Text> : null;
    $[18] = countryName;
    $[19] = hasStripeAccount;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== connectEnabled || $[22] !== country || $[23] !== countryItems || $[24] !== hasStripeAccount || $[25] !== onCountryChange) {
    t13 = !hasStripeAccount ? <Field.Root maxW="28rem" mt={2}><Field.Label>Country</Field.Label><NativeSelectRoot disabled={!connectEnabled} size="sm"><NativeSelectField aria-label="Payout country" items={countryItems} value={country} onChange={event => onCountryChange?.(event.currentTarget.value)} /></NativeSelectRoot><Field.HelperText color="colorPalette.fg/60">We support payouts to the US, Canada, UK, Switzerland, and the European Economic Area.</Field.HelperText></Field.Root> : null;
    $[21] = connectEnabled;
    $[22] = country;
    $[23] = countryItems;
    $[24] = hasStripeAccount;
    $[25] = onCountryChange;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== hasStripeAccount || $[28] !== onRemove) {
    t14 = hasStripeAccount && onRemove ? <Button alignSelf="flex-start" color="colorPalette.fg/70" fontWeight="medium" height="auto" minW={0} mt={1} px={0} size="xs" variant="plain" onClick={onRemove}>Remove Stripe account</Button> : null;
    $[27] = hasStripeAccount;
    $[28] = onRemove;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t11 || $[31] !== t12 || $[32] !== t13 || $[33] !== t14 || $[34] !== t9) {
    t15 = <VStack align="stretch" flex={1} gap={1} minW="12rem">{t9}{t11}{t12}{t13}{t14}</VStack>;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
    $[34] = t9;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <LuRefreshCw />;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== onRefresh || $[38] !== refreshLoading) {
    t17 = <IconButton aria-label="Refresh Connect status" loading={refreshLoading} size="sm" variant="outline" onClick={onRefresh}>{t16}</IconButton>;
    $[37] = onRefresh;
    $[38] = refreshLoading;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== connectEnabled || $[41] !== hasStripeAccount || $[42] !== onView || $[43] !== viewLoading) {
    t18 = hasStripeAccount && onView ? <Button disabled={!connectEnabled} loading={viewLoading} size="sm" variant="outline" onClick={onView}><LuExternalLink /> View account</Button> : null;
    $[40] = connectEnabled;
    $[41] = hasStripeAccount;
    $[42] = onView;
    $[43] = viewLoading;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  const t19 = !connectEnabled || !hasStripeAccount && !country;
  let t20;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <LuExternalLink />;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  const t21 = connectReady ? "Update account" : "Connect Stripe";
  let t22;
  if ($[46] !== onConnect || $[47] !== onboardingLoading || $[48] !== t19 || $[49] !== t21) {
    t22 = <Button disabled={t19} loading={onboardingLoading} size="sm" variant="cta" onClick={onConnect}>{t20} {t21}</Button>;
    $[46] = onConnect;
    $[47] = onboardingLoading;
    $[48] = t19;
    $[49] = t21;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== t17 || $[52] !== t18 || $[53] !== t22) {
    t23 = <HStack flexShrink={0} gap={2} justifyContent="flex-end" wrap="wrap">{t17}{t18}{t22}</HStack>;
    $[51] = t17;
    $[52] = t18;
    $[53] = t22;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== t15 || $[56] !== t23) {
    t24 = <Card.Body><HStack align="flex-start" gap={4} wrap="wrap">{t5}{t15}{t23}</HStack></Card.Body>;
    $[55] = t15;
    $[56] = t23;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== connectReady || $[59] !== isStandard) {
    t25 = connectReady && isStandard ? <Card.Footer justifyContent="flex-end"><Button asChild={true} size="sm" variant="outline"><a href="https://dashboard.stripe.com/" rel="noopener noreferrer" target="_blank"><LuExternalLink /> Open Stripe Dashboard</a></Button></Card.Footer> : null;
    $[58] = connectReady;
    $[59] = isStandard;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  let t26;
  if ($[61] !== t24 || $[62] !== t25) {
    t26 = <Card.Root size="sm" variant="outline">{t24}{t25}</Card.Root>;
    $[61] = t24;
    $[62] = t25;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  return t26;
};
export const PayoutBalanceStats = t0 => {
  const $ = _c(30);
  const {
    availableLabel,
    availableUsd,
    pendingLabel,
    pendingUsd,
    totalUsd
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      base: "column",
      md: "row"
    };
    t2 = {
      md: "1px"
    };
    t3 = {
      base: "1px",
      md: "0"
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] !== availableLabel) {
    t4 = <Stat.Label>{availableLabel}</Stat.Label>;
    $[3] = availableLabel;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== availableUsd) {
    t5 = formatUsd(availableUsd);
    $[5] = availableUsd;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t5) {
    t6 = <Stat.ValueText fontSize="2xl">{t5}</Stat.ValueText>;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t4 || $[10] !== t6) {
    t7 = <Stat.Root flex={1} padding={4}>{t4}{t6}</Stat.Root>;
    $[9] = t4;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== pendingLabel) {
    t8 = <Stat.Label>{pendingLabel}</Stat.Label>;
    $[12] = pendingLabel;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== pendingUsd) {
    t9 = formatUsd(pendingUsd);
    $[14] = pendingUsd;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t9) {
    t10 = <Stat.ValueText fontSize="2xl">{t9}</Stat.ValueText>;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t8) {
    t11 = <Stat.Root flex={1} padding={4}>{t8}{t10}</Stat.Root>;
    $[18] = t10;
    $[19] = t8;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Stat.Label>Total credited</Stat.Label>;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== totalUsd) {
    t13 = formatUsd(totalUsd);
    $[22] = totalUsd;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t13) {
    t14 = <Stat.Root flex={1} padding={4}>{t12}<Stat.ValueText fontSize="2xl">{t13}</Stat.ValueText></Stat.Root>;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t11 || $[27] !== t14 || $[28] !== t7) {
    t15 = <Flex borderWidth="1px" direction={t1} divideX={t2} divideY={t3} rounded="l2">{t7}{t11}{t14}</Flex>;
    $[26] = t11;
    $[27] = t14;
    $[28] = t7;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  return t15;
};
export const PayoutRequestCard = t0 => {
  const $ = _c(26);
  const {
    available,
    minPayout,
    amount,
    connectReady,
    loading,
    hasOpenPayout: t1,
    onAmountChange,
    onRequest
  } = t0;
  const hasOpenPayout = t1 === undefined ? false : t1;
  const canRequest = connectReady && available >= minPayout && !hasOpenPayout;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Text fontWeight="semibold">Request payout</Text>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== hasOpenPayout || $[2] !== minPayout) {
    t3 = hasOpenPayout ? "You have a payout in progress. You can request another once it is paid, rejected, or canceled." : `Minimum ${formatUsd(minPayout)}. Leave the amount blank to withdraw the full available balance. Our team approves each payout before it is sent.`;
    $[1] = hasOpenPayout;
    $[2] = minPayout;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <VStack align="stretch" gap={1}>{t2}<Text color="colorPalette.fg/60" fontFamily="longform" textStyle="sm">{t3}</Text></VStack>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Field.Label>Amount (USD)</Field.Label>;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = available > 0 ? String(available) : "";
  let t7;
  if ($[7] !== onAmountChange) {
    t7 = event => onAmountChange(event.target.value);
    $[7] = onAmountChange;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== amount || $[10] !== hasOpenPayout || $[11] !== t6 || $[12] !== t7) {
    t8 = <Field.Root maxW="11rem">{t5}<InputGroup startAddon="$"><Input disabled={hasOpenPayout} min={0} placeholder={t6} step="0.01" type="number" value={amount} onChange={t7} /></InputGroup></Field.Root>;
    $[9] = amount;
    $[10] = hasOpenPayout;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = !canRequest;
  const t10 = canRequest ? "cta" : "outline";
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <LuBanknote />;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== loading || $[16] !== onRequest || $[17] !== t10 || $[18] !== t9) {
    t12 = <Button disabled={t9} loading={loading} variant={t10} onClick={onRequest}>{t11} Request payout</Button>;
    $[15] = loading;
    $[16] = onRequest;
    $[17] = t10;
    $[18] = t9;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t12 || $[21] !== t8) {
    t13 = <HStack align="flex-end" gap={3} wrap="wrap">{t8}{t12}</HStack>;
    $[20] = t12;
    $[21] = t8;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t13 || $[24] !== t4) {
    t14 = <Card.Root size="sm" variant="outline"><Card.Body gap={4}>{t4}{t13}</Card.Body></Card.Root>;
    $[23] = t13;
    $[24] = t4;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  return t14;
};

/** The advisor's open payout: "Awaiting approval" with Cancel (L16), or "Sending". */
export const PayoutAwaitingCard = t0 => {
  const $ = _c(30);
  const {
    payout,
    canceling,
    onCancel
  } = t0;
  let t1;
  if ($[0] !== payout.amountUsd) {
    t1 = formatUsd(payout.amountUsd);
    $[0] = payout.amountUsd;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <Text fontWeight="semibold">{t1}</Text>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== payout) {
    t3 = advisorPayoutStatusPalette(payout);
    $[4] = payout;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== payout) {
    t4 = advisorPayoutStatusLabel(payout);
    $[6] = payout;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <Badge colorPalette={t3} variant="subtle">{t4}</Badge>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t2 || $[12] !== t5) {
    t6 = <HStack gap={2}>{t2}{t5}</HStack>;
    $[11] = t2;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== payout.createdAt || $[15] !== payout.requestedAt || $[16] !== payout.statusKey) {
    t7 = payout.statusKey === "pending" ? `Requested ${formatWhen(payout.requestedAt || payout.createdAt)}. Our team reviews payouts before sending them to your Stripe account — we’ll email you when it’s sent.` : "Approved and on its way to your Stripe account.";
    $[14] = payout.createdAt;
    $[15] = payout.requestedAt;
    $[16] = payout.statusKey;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t7) {
    t8 = <Text color="colorPalette.fg/70" fontFamily="longform" textStyle="sm">{t7}</Text>;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t6 || $[21] !== t8) {
    t9 = <VStack align="stretch" gap={1}>{t6}{t8}</VStack>;
    $[20] = t6;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== canceling || $[24] !== onCancel || $[25] !== payout.canCurrentUserCancel) {
    t10 = payout.canCurrentUserCancel && onCancel ? <Button loading={canceling} size="sm" variant="outline" onClick={onCancel}>Cancel request</Button> : null;
    $[23] = canceling;
    $[24] = onCancel;
    $[25] = payout.canCurrentUserCancel;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t9) {
    t11 = <Card.Root colorPalette="yellow" size="sm" variant="subtle"><Card.Body><HStack align="flex-start" gap={4} justify="space-between" wrap="wrap">{t9}{t10}</HStack></Card.Body></Card.Root>;
    $[27] = t10;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  return t11;
};
export const PayoutHistoryTable = t0 => {
  const $ = _c(10);
  const {
    payouts,
    cancelingId,
    onCancel
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Table.Header><Table.Row><Table.ColumnHeader>Requested</Table.ColumnHeader><Table.ColumnHeader>Amount</Table.ColumnHeader><Table.ColumnHeader>Status</Table.ColumnHeader><Table.ColumnHeader>Updated</Table.ColumnHeader><Table.ColumnHeader /></Table.Row></Table.Header>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== cancelingId || $[2] !== onCancel || $[3] !== payouts) {
    let t3;
    if ($[5] !== cancelingId || $[6] !== onCancel) {
      t3 = row => {
        const reviewer = advisorPayoutReviewer(row);
        const note = row.rejectionReason || row.failureReason;
        return <Table.Row key={row.advisorPayoutId || row.id}><Table.Cell>{formatWhen(row.requestedAt || row.createdAt)}</Table.Cell><Table.Cell>{formatUsd(row.amountUsd)}</Table.Cell><Table.Cell maxWidth="22rem" whiteSpace="normal"><Badge colorPalette={advisorPayoutStatusPalette(row)} variant="subtle">{advisorPayoutStatusLabel(row)}</Badge>{note ? <Text color="colorPalette.fg/70" fontFamily="longform" fontSize="xs" mt={1}>{row.rejectionReason ? `Reason: ${row.rejectionReason}` : note}</Text> : null}</Table.Cell><Table.Cell>{formatWhen(row.completedAt || row.rejectedAt || row.canceledAt || row.failedAt || row.approvedAt)}{reviewer && !row.rejectedAt ? <Text color="colorPalette.fg/55" fontSize="xs">{reviewer}</Text> : null}</Table.Cell><Table.Cell textAlign="end">{row.canCurrentUserCancel && onCancel ? <Button loading={cancelingId === row.advisorPayoutId} size="xs" variant="outline" onClick={() => onCancel(row)}>Cancel</Button> : null}</Table.Cell></Table.Row>;
      };
      $[5] = cancelingId;
      $[6] = onCancel;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    t2 = payouts.map(t3);
    $[1] = cancelingId;
    $[2] = onCancel;
    $[3] = payouts;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[8] !== t2) {
    t3 = <Box borderWidth="1px" overflowX="auto" rounded="l2"><Table.Root size="sm">{t1}<Table.Body>{t2}</Table.Body></Table.Root></Box>;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};
const HistoryBlock = t0 => {
  const $ = _c(10);
  const {
    title,
    emptyTitle,
    emptyDescription,
    isEmpty,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <Text fontWeight="semibold">{title}</Text>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== emptyDescription || $[4] !== emptyTitle || $[5] !== isEmpty) {
    t2 = isEmpty ? <EmptyState description={emptyDescription} icon={<LuHistory />} size="sm" title={emptyTitle} /> : children;
    $[2] = children;
    $[3] = emptyDescription;
    $[4] = emptyTitle;
    $[5] = isEmpty;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = <VStack align="stretch" gap={3}>{t1}{t2}</VStack>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};

/**
 * Supports both Connect models:
 * - **Standard**: direct charges → Open Stripe Dashboard
 * - **Express**: platform ledger + Request payout (transfer)
 */
export const PayoutsSection = () => {
  const [payoutAmount, setPayoutAmount] = useState('');
  const [country, setCountry] = useState('');
  const [confirmReset, setConfirmReset] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionOk, setActionOk] = useState<string | null>(null);
  const balanceQuery = useQuery<BalanceData>(ADVISOR_PAYOUT_BALANCE, {
    fetchPolicy: 'network-only'
  });
  const ledgerQuery = useQuery<LedgerData>(ADVISOR_LEDGER_ENTRIES, {
    variables: {
      pageSize: PAGE_SIZE,
      page: 1
    },
    fetchPolicy: 'network-only'
  });
  const payoutsQuery = useQuery<PayoutsData>(ADVISOR_PAYOUTS, {
    variables: {
      pageSize: PAGE_SIZE,
      page: 1
    },
    fetchPolicy: 'network-only'
  });
  const firstLedgerItems = (ledgerQuery.data?.advisorLedgerEntries?.items ?? []).filter((entry): entry is ScoAdvisorLedgerEntry => !!entry);
  const firstPayoutItems = (payoutsQuery.data?.advisorPayouts?.items ?? []).filter((payout): payout is ScoAdvisorPayout => !!payout);
  const {
    items: ledgerItems,
    loadMore: loadMoreLedger,
    loadingMore: loadingMoreLedger,
    hasNextPage: ledgerHasNext
  } = useAdvisorsCollectionLoadMore({
    fetchMore: ledgerQuery.fetchMore,
    field: 'advisorLedgerEntries',
    firstPageItems: firstLedgerItems,
    hasNextPage: ledgerQuery.data?.advisorLedgerEntries?.pagination?.hasNextPage
  });
  const {
    items: payoutItems,
    loadMore: loadMorePayouts,
    loadingMore: loadingMorePayouts,
    hasNextPage: payoutsHasNext
  } = useAdvisorsCollectionLoadMore({
    fetchMore: payoutsQuery.fetchMore,
    field: 'advisorPayouts',
    firstPageItems: firstPayoutItems,
    hasNextPage: payoutsQuery.data?.advisorPayouts?.pagination?.hasNextPage
  });
  const refetchAll = () => {
    void balanceQuery.refetch();
    void ledgerQuery.refetch();
    void payoutsQuery.refetch();
  };
  const [createOnboarding, onboardingState] = useMutation(CREATE_ADVISOR_CONNECT_ONBOARDING_LINK);
  const [createDashboardLink, dashboardState] = useMutation(CREATE_ADVISOR_CONNECT_DASHBOARD_LINK);
  const [refreshConnect, refreshState] = useMutation(REFRESH_ADVISOR_CONNECT_STATUS);
  const [resetConnect, resetState] = useMutation(RESET_ADVISOR_STRIPE_CONNECT);
  const [requestPayout, payoutState] = useMutation(REQUEST_ADVISOR_PAYOUT);
  const [cancelPayout] = useMutation(CANCEL_ADVISOR_PAYOUT);
  const [cancelingId, setCancelingId] = useState<string | null>(null);
  const openPayout = payoutItems.find(payout_0 => payout_0.statusKey === 'pending' || payout_0.statusKey === 'processing') || null;
  const balance = balanceQuery.data?.advisorPayoutBalance;
  const hasStripeAccount = Boolean(balance?.stripeConnectAccountId);
  const countries = (balance?.connectCountryOptions ?? []).filter((option): option is ConnectCountry => !!option);
  const available = Number(balance?.availableBalanceUsd ?? 0);
  const minPayout = Number(balance?.payoutMinUsd ?? DEFAULT_PAYOUT_MIN_USD);
  const connectStatus = balance?.stripeConnectStatusKey || 'not_started';
  const connectReady = connectStatus === 'enabled';
  const connectEnabled = balance?.connectEnabled !== false;
  const accountType = (balance?.connectAccountType || 'express') as string;
  const isStandard = accountType === 'standard';
  const isExpress = !isStandard && (accountType === 'express' || balance?.supportsPlatformPayout === true);
  const run = async (fn: () => Promise<void>, fallback: string) => {
    setActionError(null);
    setActionOk(null);
    try {
      await fn();
    } catch (err) {
      setActionError(cleanErrorMessage(err, fallback));
    }
  };
  const handleOnboarding = () => run(async () => {
    if (!hasStripeAccount && !country) {
      setActionError('Choose the country where you will receive payouts.');
      return;
    }
    const result = await createOnboarding({
      variables: {
        input: hasStripeAccount ? {} : {
          country
        }
      }
    });
    const url = (result.data as OnboardingLinkData | null | undefined)?.createAdvisorConnectOnboardingLink?.url;
    if (url && typeof window !== 'undefined') {
      window.location.href = url;
      return;
    }
    setActionError('No onboarding URL returned. Connect may be disabled on the API.');
  }, 'Could not start Stripe onboarding.');
  const handleReset = () => {
    setConfirmReset(false);
    return run(async () => {
      await resetConnect();
      setCountry('');
      refetchAll();
      setActionOk('Stripe account removed. Choose a country and connect again.');
    }, 'Could not remove that Stripe account.');
  };
  const handleViewAccount = () => run(async () => {
    const result_0 = await createDashboardLink();
    const url_0 = (result_0.data as DashboardLinkData | null | undefined)?.createAdvisorConnectDashboardLink?.url;
    if (url_0 && typeof window !== 'undefined') {
      window.location.href = url_0;
      return;
    }
    setActionError('No Stripe dashboard link returned.');
  }, 'Could not open your Stripe account.');
  const handleRefresh = () => run(async () => {
    await refreshConnect();
    refetchAll();
    setActionOk('Connect status refreshed.');
  }, 'Could not refresh Connect status.');
  const handlePayout = () => run(async () => {
    const amount = payoutAmount.trim() ? Number(payoutAmount) : available;
    if (!amount || amount <= 0) {
      setActionError('Enter a valid payout amount.');
      return;
    }
    if (amount < minPayout) {
      setActionError(`Minimum payout is ${formatUsd(minPayout)}.`);
      return;
    }
    if (amount > available) {
      setActionError('Amount exceeds available balance.');
      return;
    }
    try {
      await requestPayout({
        variables: {
          input: {
            amountUsd: amount
          }
        }
      });
    } catch (err_0) {
      setActionError(advisorPayoutErrorMessage(err_0, 'Could not request that payout.'));
      refetchAll();
      return;
    }
    setPayoutAmount('');
    refetchAll();
    setActionOk('Payout requested — awaiting approval. We’ll email you when it’s sent.');
  }, 'Could not request that payout.');
  const handleCancelPayout = (payout_1: ScoAdvisorPayout) => run(async () => {
    if (!payout_1.advisorPayoutId) {
      return;
    }
    setCancelingId(payout_1.advisorPayoutId);
    try {
      await cancelPayout({
        variables: {
          input: {
            advisorPayoutId: payout_1.advisorPayoutId
          }
        }
      });
      setActionOk('Payout request canceled. The amount is back in your available balance.');
    } catch (err_1) {
      setActionError(advisorPayoutErrorMessage(err_1, 'Could not cancel that payout.'));
    } finally {
      setCancelingId(null);
      refetchAll();
    }
  }, 'Could not cancel that payout.');
  const loading = balanceQuery.loading && !balance || ledgerQuery.loading && !ledgerItems.length && !ledgerQuery.data;
  const error = balanceQuery.error || ledgerQuery.error;
  return <AdvisorsQueryState error={error} loading={loading}>
      <VStack align={'stretch'} gap={6}>
        <PayoutConnectCard connectEnabled={connectEnabled} connectReady={connectReady} connectStatus={connectStatus} countries={countries} country={country} hasStripeAccount={hasStripeAccount} isStandard={isStandard} onboardingLoading={onboardingState.loading} refreshLoading={refreshState.loading} stripeConnectCountry={balance?.stripeConnectCountry} viewLoading={dashboardState.loading} onConnect={() => void handleOnboarding()} onCountryChange={setCountry} onRefresh={() => void handleRefresh()} onRemove={hasStripeAccount ? () => setConfirmReset(true) : undefined} onView={hasStripeAccount ? () => void handleViewAccount() : undefined} />
        <Dialog.Root motionPreset={'scale'} open={confirmReset} placement={'center'} size={'md'} onOpenChange={event => setConfirmReset(event.open)}>
          <Portal>
            <Dialog.Backdrop bg={'blackAlpha.700'} />
            <Dialog.Positioner paddingX={4}>
              <Dialog.Content bgColor={'colorPalette.container'} borderColor={'colorPalette.border'} borderRadius={'2xl'} borderWidth={'1px'} boxShadow={'xl'} maxWidth={'480px'} width={'full'}>
                <Dialog.Header alignItems={'flex-start'} borderBottomWidth={'1px'} borderColor={'colorPalette.border/50'} paddingBottom={4} paddingTop={5} paddingX={6} position={'relative'}>
                  <VStack align={'flex-start'} flex={1} gap={1} minWidth={0} paddingRight={10}>
                    <Dialog.Title fontSize={'xl'} fontWeight={'semibold'} lineHeight={'short'}>
                      Remove Stripe account?
                    </Dialog.Title>
                    <Dialog.Description color={'colorPalette.fg/60'} fontSize={'sm'}>
                      Earnings already in your Startups balance stay here. You will choose a country and
                      connect a new Stripe account.
                    </Dialog.Description>
                  </VStack>
                  <Dialog.CloseTrigger asChild insetEnd={3} position={'absolute'} top={3}>
                    <CloseButton size={'sm'} />
                  </Dialog.CloseTrigger>
                </Dialog.Header>
                <Dialog.Body paddingX={6} paddingY={5}>
                  <Text color={'colorPalette.fg/80'} fontFamily={'longform'} textStyle={'sm'}>
                    Stripe cannot change the country on an account that already exists. Remove this one only
                    when it has no balance waiting at Stripe.
                  </Text>
                </Dialog.Body>
                <Dialog.Footer borderColor={'colorPalette.border/50'} borderTopWidth={'1px'} gap={2} paddingX={6} paddingY={4}>
                  <Button variant={'ghost'} onClick={() => setConfirmReset(false)}>
                    Cancel
                  </Button>
                  <Button loading={resetState.loading} variant={'cta'} onClick={() => void handleReset()}>
                    Remove account
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Portal>
        </Dialog.Root>

        <PayoutBalanceStats availableLabel={isExpress ? 'Available to payout' : 'Earnings (log)'} availableUsd={balance?.availableBalanceUsd} pendingLabel={isExpress ? 'Pending holdback' : 'Pending'} pendingUsd={balance?.pendingBalanceUsd} totalUsd={balance?.totalBalanceUsd} />

        {isExpress && openPayout ? <PayoutAwaitingCard canceling={cancelingId === openPayout.advisorPayoutId} payout={openPayout} onCancel={() => void handleCancelPayout(openPayout)} /> : null}

        {isExpress ? <PayoutRequestCard amount={payoutAmount} available={available} connectReady={connectReady} hasOpenPayout={!!openPayout} loading={payoutState.loading} minPayout={minPayout} onAmountChange={setPayoutAmount} onRequest={() => void handlePayout()} /> : null}

        {actionError || actionOk ? <Text color={actionError ? 'red.fg' : 'green.fg'} fontFamily={'longform'} fontSize={'sm'}>
            {actionError || actionOk}
          </Text> : null}

        <HistoryBlock emptyDescription={'Completed paid sessions credit your balance here after the holdback.'} emptyTitle={'No session earnings yet'} isEmpty={ledgerItems.length === 0} title={'Session earnings'}>
          <Box borderWidth={'1px'} overflowX={'auto'} rounded={'l2'}>
            <Table.Root size={'sm'}>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>When</Table.ColumnHeader>
                  <Table.ColumnHeader>Type</Table.ColumnHeader>
                  <Table.ColumnHeader>Amount</Table.ColumnHeader>
                  {isExpress && <Table.ColumnHeader>Available</Table.ColumnHeader>}
                  <Table.ColumnHeader>Note</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {ledgerItems.map(row => <Table.Row key={row.advisorLedgerEntryId || row.id}>
                    <Table.Cell>{formatWhen(row.createdAt)}</Table.Cell>
                    <Table.Cell>{row.entryType?.title || row.entryTypeKey || '—'}</Table.Cell>
                    <Table.Cell>{formatUsd(row.amountUsd)}</Table.Cell>
                    {isExpress && <Table.Cell>{formatWhen(row.availableAt)}</Table.Cell>}
                    <Table.Cell>
                      <Text color={'colorPalette.fg/60'} fontSize={'xs'} lineClamp={2}>
                        {row.description || '—'}
                      </Text>
                    </Table.Cell>
                  </Table.Row>)}
              </Table.Body>
            </Table.Root>
          </Box>
          <AdvisorsLoadMore hasNextPage={ledgerHasNext} loading={loadingMoreLedger} onLoadMore={loadMoreLedger} />
        </HistoryBlock>

        {isExpress ? <HistoryBlock emptyDescription={'Payout requests and their approval status will show up here.'} emptyTitle={'No payouts requested yet'} isEmpty={payoutItems.length === 0} title={'Payout history'}>
            <PayoutHistoryTable cancelingId={cancelingId} payouts={payoutItems} onCancel={payout_2 => void handleCancelPayout(payout_2)} />
            <AdvisorsLoadMore hasNextPage={payoutsHasNext} loading={loadingMorePayouts} onLoadMore={loadMorePayouts} />
          </HistoryBlock> : null}
      </VStack>
    </AdvisorsQueryState>;
};
function _temp(option_0) {
  return option_0.code && option_0.name ? [{
    value: option_0.code,
    label: option_0.name
  }] : [];
}